import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleUnit } from './equipmentTypes';
import { useFormsContext } from '../../contexts/FormsContext';
import { formatDateDisplay } from '../../utils/helperFunctions';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import {
    buildUnitReportsPath,
    seedUnitReportFilters,
} from '../../utils/unitReportsUtils';
import {
    fetchLatestInspectionReportForUnit,
    resolveInspectionReportDisplayDate,
    resolveUMCounterFromInspectionReport,
} from '../../utils/reportUtils';
import { getEquipmentReportStatusLabel, seedSelectedUnitForInspect } from './equipmentUnitSnapshotUtils';
import { Btn } from '../ui';

// Inspections Tab Component
export const EquipmentInspectionsTab = ({ unit }: { unit: SingleUnit }) => {
    const navigate = useNavigate();
    const { companyForms } = useFormsContext();
    const [lastInspectionReport, setLastInspectionReport] = useState<any>(null);
    const [inspectionSnapshotReady, setInspectionSnapshotReady] = useState(false);

    const inspectionFormName = companyForms.find((f) => f.id === unit.category?.formId)?.name || null;
    const hasInspectionForm = !!unit.category?.formId;
    const lastInspectionDay =
        resolveInspectionReportDisplayDate(lastInspectionReport) ?? unit.lastInspectionDate ?? null;
    const hasInspectionHistory = Boolean(lastInspectionDay);

    const lastInspectionUm =
        unit.currentUMCounterValue ??
        resolveUMCounterFromInspectionReport(lastInspectionReport) ??
        null;

    useEffect(() => {
        let isCancelled = false;
        setInspectionSnapshotReady(false);
        setLastInspectionReport(null);

        if (!hasInspectionForm) {
            setInspectionSnapshotReady(true);
            return () => {
                isCancelled = true;
            };
        }

        (async () => {
            try {
                const report = await fetchLatestInspectionReportForUnit(unit.id);
                if (!isCancelled && report) {
                    setLastInspectionReport(report);
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Error fetching last inspection report:', err);
                }
            } finally {
                if (!isCancelled) {
                    setInspectionSnapshotReady(true);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [unit.id, hasInspectionForm]);

    const handleCreateInspection = () => {
        if (!unit.categoryId) return;
        seedSelectedUnitForInspect(unit);
        navigate(`/inspect/${unit.categoryId}?unitId=${unit.id}&action=inspect&returnTo=equipment`);
    };

    const handleViewReports = () => {
        if (!unit.categoryId) {
            return;
        }
        seedUnitReportFilters(unit.categoryId, unit.id);
        localStorage.setItem('reportsActiveTab', 'inspection');
        navigate(buildUnitReportsPath(unit.id, { tab: 'inspection', equipmentCategoryId: unit.categoryId }));
    };

    const inspectionStatus = getEquipmentReportStatusLabel(lastInspectionReport);

    return (
        <div className="inspections-tab">
            <div className="snapshots-grid">
                <div className="snapshot-card">
                    <div className="snapshot-header">
                        <h3>Inspection</h3>
                        {inspectionFormName && <span className="snapshot-form-badge">{inspectionFormName}</span>}
                    </div>

                    {!hasInspectionForm ? (
                        <div className="snapshot-empty-state">
                            <span className="empty-icon">📋</span>
                            <p>No inspection form assigned to this category.</p>
                            <p className="empty-hint">Assign a form in the Equipment Category settings.</p>
                        </div>
                    ) : !inspectionSnapshotReady ? (
                        <div className="snapshot-empty-state">
                            <p>Loading inspection summary…</p>
                        </div>
                    ) : !hasInspectionHistory ? (
                        <div className="snapshot-empty-state">
                            <span className="empty-icon">🔍</span>
                            <p>No inspections performed yet.</p>
                            <p className="empty-hint">Perform an inspection to view results here.</p>
                        </div>
                    ) : (
                        <div className="snapshot-content">
                            <div className="snapshot-row">
                                <span className="snapshot-label">Last Inspection</span>
                                <span className="snapshot-value">{formatDateDisplay(lastInspectionDay)}</span>
                            </div>
                            {lastInspectionUm != null && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Last {displayUnitOfMeasure(unit.unitOfMeasure) || 'UM'}</span>
                                    <span className="snapshot-value">
                                        {lastInspectionUm.toLocaleString()}
                                    </span>
                                </div>
                            )}
                            {lastInspectionReport?.userName && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Inspected By</span>
                                    <span className="snapshot-value">{lastInspectionReport.userName}</span>
                                </div>
                            )}
                            {inspectionStatus && (
                                <div className="snapshot-row">
                                    <span className="snapshot-label">Status</span>
                                    <span className={`snapshot-value ${inspectionStatus.className}`}>
                                        {inspectionStatus.text}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="snapshot-actions">
                        {hasInspectionHistory && (
                            <Btn variant="secondary" buttonText="View Reports" buttonOnClick={handleViewReports} />
                        )}
                        {hasInspectionForm && (
                            <Btn variant="primary" buttonText="Perform Inspection" buttonOnClick={handleCreateInspection} />
                        )}
                    </div>
                </div>
            </div>

            {hasInspectionHistory && (
                <div className="report-history-link">
                    <Btn
                        variant="link"
                        buttonText="View Complete Report History in Reports Module →"
                        buttonOnClick={handleViewReports}
                    />
                </div>
            )}
        </div>
    );
};
